import {
  type JSX,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useToasterActions } from '@opositandopn/ui';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';

import { BootStatus } from '../components/core/BootStatus';
import { useGetSubscriptionStatus } from '../features/subscriptions/api/useSubscriptions';
import { useGetSelf } from '../features/users/api/useUsers';
import { useAuth } from './AuthContext';
import { useFirebase } from './FirebaseContext';

export interface NotificationsProfile {
  email: boolean;
  push: boolean;
}

export interface ConsentProfile {
  ad: boolean;
  analytics: boolean;
  functionality: boolean;
  personalization: boolean;
  security: boolean;
}

export interface UserAgreementProfile {
  accepted: boolean;
  version: number;
}

export interface SchoolProfile {
  id: string;
  name: string;
}

export interface SubscriptionProfile {
  isActive: boolean;
  isTrial: boolean;
}

export interface UserProfile {
  id?: string;
  identityProviderId?: string;
  email?: string;
  active?: boolean;
  banned?: boolean;
  username?: string;
  role?: string;
  gender?: 'male' | 'female';
  name?: string;
  avatarUrl?: string;
  bio?: string;
  notifications?: NotificationsProfile;
  consent?: ConsentProfile;
  credits?: number;
  ai?: number;
  userAgreement?: UserAgreementProfile;
  school?: SchoolProfile;
  subscription?: SubscriptionProfile;
  signInMethod?: string;
}

interface ProfileProps {
  profile: UserProfile;
  setProfile: (value: UserProfile) => void;
}

const COLLECTION_NAME = 'users_v2';

export const ProfileContext = createContext<ProfileProps | null>(null);

export const ProfileContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [profile, setProfile] = useState<UserProfile>();

  const { sendMessage } = useToasterActions();
  const { firebaseApp, analytics } = useFirebase();
  const { auth, user } = useAuth();

  const {
    data: userResponse,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetSelf(user !== null);
  const { data: subscriptionResponse } = useGetSubscriptionStatus(
    {
      userId: profile?.id ?? '',
    },
    profile !== undefined && profile.id !== undefined,
  );

  useEffect(() => {
    if (userResponse) {
      setProfile((prevProfile) => ({
        ...prevProfile,
        id: userResponse.id,
        identityProviderId: userResponse.identityProviderId,
        email: userResponse.email,
        active: userResponse.active,
        banned: userResponse.banned,
        username: userResponse.username,
        name: userResponse.name,
        avatarUrl: userResponse.avatarUrl,
        credits: userResponse.credits,
        role: userResponse.role,
        gender: userResponse.gender,
        bio: userResponse.bio,
        notifications: userResponse.notifications,
        consent: userResponse.consent,
        userAgreement: userResponse.userAgreement,
        school: userResponse.school,
      }));
    }
  }, [userResponse]);

  useEffect(() => {
    if (subscriptionResponse) {
      setProfile((prevProfile) => ({
        ...prevProfile,
        subscription: {
          isActive: subscriptionResponse.isActive,
          isTrial: subscriptionResponse.isTrial,
        },
      }));
    }
  }, [subscriptionResponse]);

  useEffect(() => {
    if (user === null) {
      setProfile(undefined);
      return;
    }

    refetch();

    const firestore = getFirestore(firebaseApp);
    let signInMethod: string | undefined = undefined;

    if (
      user.providerData.some((provider) => provider.providerId === 'google.com')
    ) {
      signInMethod = 'google.com';
    } else {
      signInMethod = 'email';
    }

    const unsubscribe = onSnapshot(
      doc(firestore, COLLECTION_NAME, user.uid),
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }

        // Laid over the profile rather than put in its place. The document holds most
        // of the profile but not all of it, and replacing meant every field only the
        // API answers for was dropped on each snapshot — `subscription` above all,
        // which every premium gate reads, so a routine write like a credit change
        // read to a paying user as though their subscription had lapsed, until they
        // reloaded. The cost of merging is the other direction: a field the document
        // drops keeps its last value here until the API's next answer clears it.
        setProfile((prevProfile) => ({
          ...prevProfile,
          ...snapshot.data(),
          signInMethod,
          // Still pinned to what the API gave us: the whole boot is gated on this id,
          // and it is the one field that has to mean "the API has answered".
          id: prevProfile?.id,
        }));
      },
    );

    return () => unsubscribe();
  }, [firebaseApp, refetch, user]);

  // Asked of the API's answer rather than of the profile, because the API is the
  // register of who has finished signing up and the profile document is not. That
  // document carries no `username` field at all whenever the column behind it is null
  // — the backend deletes the field rather than writing an empty one — so reading the
  // question off the merged profile let a snapshot answer it, and signed out users the
  // API had just returned a username for.
  useEffect(() => {
    if (userResponse === undefined) {
      return;
    }

    if (
      userResponse.username === undefined ||
      userResponse.username === null ||
      userResponse.username === ''
    ) {
      sendMessage(
        'Tu usuario no está registrado',
        'Antes de iniciar sesión, debes registrarte en la plataforma y completar tu perfil, haz click en "Regístrate" para crear una cuenta.',
        'danger',
      );
      auth.signOut();
    }
  }, [auth, sendMessage, userResponse]);

  useEffect(() => {
    if (analytics === undefined || profile === undefined) {
      return;
    }

    setAnalyticsCollectionEnabled(
      analytics,
      profile.consent?.analytics === true,
    );
  }, [analytics, profile]);

  // Memoised because every useProfile() consumer re-renders when this identity
  // changes, and a literal here changes it on every render of this provider.
  const value = useMemo(() => ({ profile: profile!, setProfile }), [profile]);

  // Gated on `id` rather than on the profile as a whole, because the profile has two
  // sources and only one of them carries an id. The Firestore snapshot answers in a few
  // hundred milliseconds — long before this query finishes retrying — and writes
  // `id: prevProfile?.id`, which is undefined when the API has not answered. Gating on
  // the profile therefore let a failed /users/me through: the app booted looking signed
  // in, with no internal user id, which silently disables the subscription query and
  // reads to the user as "not premium". A retry screen is the better of the two.
  //
  // `isLoading` goes false when the query gives up while the id stays undefined, so
  // without `isError` those two states are one, and a request that had already failed
  // its last retry held the splash for as long as the user was willing to look at it.
  if (profile?.id === undefined || isLoading) {
    return (
      <BootStatus
        failed={isError}
        message="No hemos podido cargar tu perfil. Comprueba tu conexión e inténtalo de nuevo."
        onRetry={() => {
          refetch();
        }}
        retrying={isFetching}
      />
    );
  }

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);

  if (!context) {
    throw new Error('useProfile must be used within a ProfileContextProvider');
  }

  return context;
};

/**
 * Whether the user is entitled to premium content: a subscription that is active and
 * past its trial. Every gate in the app asks this same question — ask it here.
 */
export const useIsPremium = () => {
  const { profile } = useProfile();

  return !!profile.subscription?.isActive && !profile.subscription?.isTrial;
};
